import { db, storage, auth } from "./firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  writeBatch,
  onSnapshot,
  serverTimestamp,
  type QuerySnapshot,
  type DocumentData,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import type { Issue, IssueStatus } from "@/types/issue";

const ISSUES_COLLECTION = 'issues';

// Create a new issue with attachments
export const createIssue = async (
  issue: Omit<Issue, 'id' | 'createdAt' | 'updatedAt'>, 
  attachments?: File[]
): Promise<Issue> => {
  try {
    const issueRef = doc(collection(db, ISSUES_COLLECTION));
    const timestamp = serverTimestamp();
    
    // Upload attachments if any
    const uploadedAttachments = [];
    if (attachments && attachments.length > 0) {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('User must be authenticated to upload attachments');
      }

      for (const file of attachments) {
        const attachmentId = crypto.randomUUID();
        const storageRef = ref(storage, `users/${currentUser.uid}/issues/${issueRef.id}/attachments/${attachmentId}`);
        
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        uploadedAttachments.push({
          id: attachmentId,
          url,
          name: file.name,
          type: file.type,
          createdAt: new Date().toISOString(),
        });
      }
    }

    const newIssue: Issue = {
      ...issue,
      id: issueRef.id,
      createdAt: timestamp,
      updatedAt: timestamp,
      comments: issue.comments || [],
      attachments: uploadedAttachments,
      activity: issue.activity || [{
        id: crypto.randomUUID(),
        type: 'update',
        content: 'Issue created',
        createdAt: new Date().toISOString(),
        createdBy: issue.createdBy
      }]
    };

    // Create the document first
    await setDoc(issueRef, {
      ...newIssue,
      attachments: uploadedAttachments,
    });

    // Return the issue with the uploaded attachments
    return {
      ...newIssue,
      attachments: uploadedAttachments,
    };
  } catch (error) {
    console.error('Error creating issue:', error);
    throw new Error('Failed to create issue');
  }
};

// Get all issues
export const getIssues = async (): Promise<Issue[]> => {
  try {
    const q = query(
      collection(db, ISSUES_COLLECTION),
      where('status', '!=', 'Archived'),
      orderBy('status'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Issue));
  } catch (error) {
    console.error('Error getting issues:', error);
    throw new Error('Failed to get issues');
  }
};

// Get a single issue by ID
export const getIssueById = async (issueId: string): Promise<Issue | null> => {
  try {
    const docRef = doc(db, ISSUES_COLLECTION, issueId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    return {
      id: docSnap.id,
      ...docSnap.data()
    } as Issue;
  } catch (error) {
    console.error('Error getting issue:', error);
    throw new Error('Failed to get issue');
  }
};

// Update an issue
export const updateIssue = async (
  issueId: string, 
  updates: Partial<Issue>,
  activityLog?: Issue['activity'][0]
): Promise<void> => {
  try {
    const issueRef = doc(db, ISSUES_COLLECTION, issueId);
    const timestamp = serverTimestamp();
    
    // Get current issue data first
    const currentIssue = await getDoc(issueRef);
    if (!currentIssue.exists()) {
      throw new Error('Issue not found');
    }

    const currentData = currentIssue.data();
    
    // Create update data with current data as base
    let updateData = {
      ...currentData,
      ...updates,
      updatedAt: timestamp,
      // Ensure we preserve existing activity and add new ones
      activity: updates.activity || [...(currentData.activity || []), ...(activityLog ? [activityLog] : [])]
    };

    // Remove any undefined values recursively
    const removeUndefined = (obj: any) => {
      Object.keys(obj).forEach(key => {
        if (obj[key] === undefined) {
          delete obj[key];
        } else if (obj[key] && typeof obj[key] === 'object') {
          removeUndefined(obj[key]);
        }
      });
      return obj;
    };

    // Clean the update data
    updateData = removeUndefined(updateData);

    await setDoc(issueRef, updateData, { merge: true });
  } catch (error) {
    console.error('Error updating issue:', error);
    throw new Error('Failed to update issue');
  }
};

// Delete issue and its attachments
export const deleteIssue = async (issue: Issue): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to delete issue');
    }

    // Delete all attachments from storage if they exist
    if (issue.attachments && issue.attachments.length > 0) {
      for (const attachment of issue.attachments) {
        const storageRef = ref(storage, `users/${currentUser.uid}/issues/${issue.id}/attachments/${attachment.id}`);
        await deleteObject(storageRef);
      }
    }
    
    // Delete the issue document
    await deleteDoc(doc(db, ISSUES_COLLECTION, issue.id));
  } catch (error) {
    console.error('Error deleting issue:', error);
    throw new Error('Failed to delete issue');
  }
};

// Archive an issue
export const archiveIssue = async (issue: Issue, userId: string): Promise<void> => {
  try {
    const timestamp = new Date().toISOString();
    await updateIssue(
      issue.id,
      {
        status: 'Archived' as IssueStatus,
        archivedAt: timestamp,
      },
      {
        id: crypto.randomUUID(),
        type: 'status_change',
        content: 'Issue archived',
        createdAt: timestamp,
        createdBy: {
          id: userId,
          name: issue.updatedBy?.name || issue.createdBy.name,
          email: issue.updatedBy?.email || issue.createdBy.email,
          photoURL: issue.updatedBy?.photoURL || issue.createdBy.photoURL,
        },
        metadata: {
          oldValue: issue.status,
          newValue: 'Archived'
        }
      }
    );
  } catch (error) {
    console.error('Error archiving issue:', error);
    throw new Error('Failed to archive issue');
  }
};

// Update issue status
export const updateIssueStatus = async (
  issue: Issue,
  newStatus: IssueStatus,
  userId: string
): Promise<void> => {
  try {
    const timestamp = new Date().toISOString();
    await updateIssue(
      issue.id,
      {
        status: newStatus,
        ...(newStatus === 'Done' ? { completedAt: timestamp } : {}),
      },
      {
        id: crypto.randomUUID(),
        type: 'status_change',
        content: `Changed status to ${newStatus}`,
        createdAt: timestamp,
        createdBy: {
          id: userId,
          name: issue.updatedBy?.name || issue.createdBy.name,
          email: issue.updatedBy?.email || issue.createdBy.email,
          photoURL: issue.updatedBy?.photoURL || issue.createdBy.photoURL,
        },
        metadata: {
          oldValue: issue.status,
          newValue: newStatus
        }
      }
    );
  } catch (error) {
    console.error('Error updating issue status:', error);
    throw new Error('Failed to update issue status');
  }
};

// Add a comment to an issue
export const addComment = async (
  issueId: string,
  comment: Omit<Issue['comments'][0], 'id'>,
): Promise<void> => {
  try {
    const issue = await getIssueById(issueId);
    if (!issue) {
      throw new Error('Issue not found');
    }

    const newComment = {
      ...comment,
      id: crypto.randomUUID(),
    };

    await updateIssue(
      issueId,
      {
        comments: [...issue.comments, newComment],
      },
      {
        id: crypto.randomUUID(),
        type: 'comment',
        content: 'Added comment',
        createdAt: comment.createdAt,
        createdBy: comment.createdBy,
      }
    );
  } catch (error) {
    console.error('Error adding comment:', error);
    throw new Error('Failed to add comment');
  }
};

// Subscribe to issues (real-time updates)
export const subscribeToIssues = (
  callback: (issues: Issue[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(
    collection(db, ISSUES_COLLECTION),
    where('status', '!=', 'Archived'),
    orderBy('status'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (querySnapshot: QuerySnapshot<DocumentData>) => {
      const issues = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Issue));
      callback(issues);
    },
    (error) => {
      console.error('Error subscribing to issues:', error);
      onError?.(error);
    }
  );
};

// Get archived issues
export const getArchivedIssues = async (): Promise<Issue[]> => {
  try {
    const q = query(
      collection(db, ISSUES_COLLECTION),
      where('status', '==', 'Archived')
    );
    
    const querySnapshot = await getDocs(q);
    // Sort the results in memory instead of in the query
    const issues = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Issue));
    
    // Sort by updatedAt in memory
    return issues.sort((a, b) => {
      const dateA = a.updatedAt?.toDate?.() || new Date(a.updatedAt);
      const dateB = b.updatedAt?.toDate?.() || new Date(b.updatedAt);
      return dateB.getTime() - dateA.getTime();
    });
  } catch (error) {
    console.error('Error getting archived issues:', error);
    throw new Error('Failed to get archived issues');
  }
}; 